// v53 — THE RAIL. Transcribed from the client's sandbox, not designed by us.
//
// ── Provenance ─────────────────────────────────────────────────────────────
//
// Source: Prototype-Chunks/sandbox.js, the `TIERS` object. Every label, every
// icon key, every badge and the section grouping are copied verbatim from
// there. It is the client's own prototype and therefore the single source of
// truth for what the rail CONTAINS.
//
// This file previously did not exist. The rail was built from `navAreas.ts` —
// an eight-area taxonomy we carried forward from v1 and never reconciled with
// the prototype. That was the whole divergence: not styling drift, a different
// taxonomy. So the rule here is transcription, not adaptation. If the label
// reads "Entire pool list", it says "Entire pool list", whatever we would have
// called it.
//
// ── Routes are DESTINATIONS, never redirects ──────────────────────────────
//
// Each route below is where the page actually lives. The first cut mapped by
// label — "Today" to /today, "Fee model" to /payments/fees — and six of those
// were redirects. A rail item pointing at a redirect mounts the target, then
// unmounts and remounts it at the real path: a visible double render on every
// click, and on every landing. That was the jitter.
//
// Where our route table disagreed with the rail, the ROUTE moved: /today now
// renders Today and /dashboard redirects to it, rather than the reverse. The
// URL should read like the nav item that led to it.
//
// Where a destination carries a query string (/payments?tab=fees), that is the
// real address of that panel, so the rail carries it in full.
//
// ── The only editorial layer ───────────────────────────────────────────────
//
// The prototype's hrefs are its own filenames (`10-find-projects.html`), so
// each item carries `route` instead — our path. Where we have no screen yet,
// `soon: true` points it at a placeholder rather than dropping the item. A
// dropped item is invisible; a placeholder is countable, and the gap between
// the prototype and the product stays legible on screen.
//
// Every `soon` below is a screen this product genuinely does not have. The
// owner and consultant tiers are net-new, which is why they carry most of them.
//
// ── The section model, in full ─────────────────────────────────────────────
//
// A section with `g` renders its name as a heading; a section without one
// renders a divider. Four sections, two of them named. That is the entire
// Discover/Deliver model — there is nothing else to it.

export type RailIcon =
  | "today" | "inbox" | "find" | "projects" | "compliance" | "payments"
  | "insights" | "community" | "settings" | "tenders" | "design" | "deals"
  | "bookings" | "siteops" | "calendar";

export interface RailItem {
  label: string;
  route: string;
  icon: RailIcon;
  /** Copied from the prototype. Live counts replace these where we have them. */
  badge?: number;
  /** No screen yet — routes to the placeholder. */
  soon?: boolean;
  /** Renders as an expandable row. The parent is a disclosure, not a
      destination — clicking it opens the list rather than navigating away,
      which is what the owner rail needs for Schemes. */
  children?: RailItem[];
}

export interface RailSection {
  /** Named → heading. Absent → divider. */
  g?: string;
  items: RailItem[];
}

export type RailTier = "tier1" | "mep" | "operative" | "consultant" | "owner";

/**
 * TIER-1 CONTRACTORS — twenty items, the longest rail.
 *
 * Six Discover items, not five: `Entire pool list` sits alongside `Find labour`
 * because searching for labour and browsing the whole pool are different jobs.
 */
const tier1: RailSection[] = [
  { items: [
    { label: "Today",                route: "/today",             icon: "today" },
    { label: "Inbox",                route: "/inbox",             icon: "inbox", badge: 5 },
  ]},
  { g: "Discover", items: [
    { label: "Find suppliers",       route: "/post-requirement",  icon: "find" },
    /* "Entire supply pool" — stakeholder-confirmed as the All Suppliers
       surface. The prototype's rail says "Entire pool list" and the discovery
       page's own <h1> says "Find suppliers"; two names for one screen was the
       inconsistency flagged in the Discovery Circuit doc. This is the decided
       one, and the page header now reads the same. */
    { label: "Entire pool list",     route: "/discovery",         icon: "community" },
    /* LIVE as of v57. Packages other buyers have put in front of THIS company
       — a Tier-1 is a buyer to its subcontractors and a supplier to the owner
       above it, so it needs both halves of the market on its rail. */
    { label: "Find opportunities",   route: "/find-opportunities", icon: "find" },
  ]},
  { g: "Deliver", items: [
    { label: "Active projects",      route: "/projects",          icon: "projects" },
    { label: "Active deals",         route: "/deals",             icon: "deals" },
    { label: "Bookings",             route: "/bookings",          icon: "bookings", soon: true },
    { label: "Site ops & diary",     route: "/projects/site-ops", icon: "siteops" },
    { label: "Calendar",             route: "/futures", icon: "calendar" },
  ]},
  { items: [
    { label: "Compliance",           route: "/compliance",        icon: "compliance" },
    { label: "Payments",             route: "/payments",          icon: "payments" },
    { label: "Fee model",            route: "/payments?tab=fees",     icon: "payments" },
    { label: "Insights",             route: "/insights",          icon: "insights" },
    { label: "Community",            route: "/community",         icon: "community" },
    { label: "Subscription",         route: "/settings?section=billing",  icon: "deals" },
    { label: "Settings",             route: "/settings",          icon: "settings" },
  ]},
];

/** MEP SUBCONTRACTORS — supplies. No Insights, no Community, per the prototype. */
const mep: RailSection[] = [
  { items: [
    { label: "Today",                route: "/today",             icon: "today" },
    { label: "Inbox",                route: "/inbox",             icon: "inbox", badge: 2 },
  ]},
  { g: "Discover", items: [
    { label: "Find projects",        route: "/find-projects",     icon: "projects", soon: true },
    /* FIND OPPORTUNITIES IS BACK, and the reversal is the client's.
       It was removed in v57 on the reading that the open market is Tier-1 only
       and a subcontractor is only ever CHOSEN. The client answered the opposite
       on 14 Sep (Q2): MEP subs and operatives should be able to find work too.

       What makes it safe now is that the rail is no longer doing the gating.
       Until migration 048 the market had no tier condition at all — a missing
       navigation entry was the only thing keeping subcontractors out, and a
       missing link is not a boundary. A package now names the tiers it is
       published to, and the market query tests the caller's SERVER-VALIDATED
       acting tier against that list. So a subbie sees a package only when the
       publisher chose to reach one layer further down. */
    { label: "Find opportunities",   route: "/find-opportunities", icon: "find" },
    { label: "Find crew",            route: "/partners",       icon: "community" },
    { label: "Find tenders",         route: "/tenders",           icon: "tenders" },
  ]},
  { g: "Deliver", items: [
    /* The supplier half of the circuit: requirements dispatched to this
       company. The prototype calls it "My bids"; the route is /opportunities
       because that is what the record is — a bid is what you send back. */
    { label: "My bids",              route: "/opportunities", icon: "deals" },
    { label: "Active projects",      route: "/projects",          icon: "projects" },
    { label: "Site ops & diary",     route: "/projects/site-ops", icon: "siteops" },
  ]},
  { items: [
    { label: "Compliance",           route: "/compliance",        icon: "compliance" },
    { label: "Payments",             route: "/payments",          icon: "payments" },
    { label: "Fee model",            route: "/payments?tab=fees",     icon: "payments" },
  ]},
];

/**
 * OPERATIVES & TRADES — the only rail whose named section is not Discover or
 * Deliver. An operative neither discovers a supply chain nor delivers into one;
 * they do "Your work". Home is Find work, not Today.
 */
const operative: RailSection[] = [
  { items: [
    { label: "Find work",            route: "/requirements",         icon: "find" },
  ]},
  { g: "Your work", items: [
    { label: "My shifts",            route: "/my-shifts",         icon: "today", soon: true },
    { label: "Site diary",           route: "/projects/site-ops", icon: "siteops" },
  ]},
  { items: [
    { label: "My wallet",            route: "/compliance",        icon: "compliance" },
    { label: "Messages",             route: "/inbox",             icon: "inbox", badge: 2 },
    { label: "Pay",                  route: "/payments",          icon: "payments" },
    { label: "Fee model",            route: "/payments?tab=fees",     icon: "payments" },
    { label: "Profile",              route: "/settings",                icon: "settings" },
  ]},
];

/** DESIGN CONSULTANTS — net-new tier. "Messages", not "Inbox". */
const consultant: RailSection[] = [
  { items: [
    { label: "Today",                route: "/today",             icon: "today" },
    { label: "Messages",             route: "/inbox",             icon: "inbox" },
  ]},
  { g: "Discover", items: [
    { label: "Opportunities",        route: "/opportunities",     icon: "find" },
    { label: "Find design briefs",   route: "/designs",           icon: "design" },
  ]},
  { g: "Deliver", items: [
    { label: "Engagements",          route: "/engagements",       icon: "projects", soon: true },
    { label: "Design Intelligence",  route: "/design-intelligence", icon: "insights", soon: true },
    { label: "Practice",             route: "/practice",          icon: "community", soon: true },
  ]},
  { items: [
    { label: "Fee model",            route: "/payments?tab=fees",     icon: "payments" },
  ]},
];

/**
 * OWNERS & OPERATORS — net-new, and the shortest rail. Ten items, and no
 * upward-facing ones at all: an owner originates, so nothing here is about
 * winning work. That absence is the tier's defining property.
 */
const owner: RailSection[] = [
  { items: [
    { label: "Today",                route: "/today",             icon: "today" },
  ]},
  /* SCHEMES IS A DISCLOSURE, NOT A DESTINATION.
     The prototype sends "Schemes" to a whole screen that then offers three
     choices. One of those — Find a consultant — is a tier we cut, and another
     is where the owner actually spends their time. So the rail opens instead of
     navigating: fewer clicks, and it stops advertising a tier that does not
     exist for us. */
  { g: "Discover", items: [
    { label: "Schemes", route: "/schemes", icon: "projects", children: [
      { label: "Your schemes",      route: "/schemes",        icon: "projects" },
      { label: "Author a brief",    route: "/schemes/author", icon: "find" },
    ]},
  ]},
  { g: "Deliver", items: [
    { label: "Assurance",            route: "/compliance",        icon: "compliance" },
  ]},
  { items: [
    { label: "Subscription",         route: "/settings?section=billing",  icon: "deals" },
    { label: "Fee model",            route: "/payments?tab=fees", icon: "payments" },
    { label: "Settings",             route: "/settings",          icon: "settings" },
  ]},
];

export const RAILS: Record<RailTier, RailSection[]> = {
  tier1, mep, operative, consultant, owner,
};

/** Where each tier lands. The operative's is the only one that is not Today. */
export const TIER_HOME: Record<RailTier, string> = {
  tier1: "/today",
  mep: "/today",
  operative: "/find-work",
  consultant: "/today",
  owner: "/today",
};

export function railFor(tier: string | null | undefined): RailSection[] {
  return RAILS[(tier as RailTier)] ?? RAILS.tier1;
}

/**
 * Every placeholder route, so App.tsx can register them in one loop rather
 * than fifteen hand-written lines that drift from this file.
 */
export const SOON_ROUTES: { route: string; label: string }[] = (() => {
  const seen = new Map<string, string>();
  for (const sections of Object.values(RAILS)) {
    for (const sec of sections) {
      /* Walk children too. A `soon` item nested under an expandable row was
         invisible here, so its placeholder route was never registered and the
         link 404'd — which is exactly what Scheme engagement did. */
      const walk = (items: RailItem[]) => {
        for (const item of items) {
          if (item.soon && !seen.has(item.route)) seen.set(item.route, item.label);
          if (item.children?.length) walk(item.children);
        }
      };
      walk(sec.items);
    }
  }
  return [...seen].map(([route, label]) => ({ route, label }));
})();
